import axiosInstance from "@helper/interceptor";

/**
 * Cookie Consent API Service
 * Handles backend synchronization of cookie consent preferences
 */

/**
 * Get user's cookie consent preferences from backend
 * @returns {Promise<Object>} Cookie consent preferences
 */
export const getCookieConsentFromBackend = async () => {
  try {
    const response = await axiosInstance.get("users/me/cookie-consent/");
    return response.data;
  } catch (error) {
    // If user is not authenticated or endpoint doesn't exist, return null
    if (error.response?.status === 401 || error.response?.status === 404) {
      return null;
    }
    console.error("Error fetching cookie consent from backend:", error);
    throw error;
  }
};

/**
 * Save user's cookie consent preferences to backend
 * @param {Object} consent - Cookie consent preferences
 * @returns {Promise<Object>} Saved consent preferences
 */
export const saveCookieConsentToBackend = async (consent) => {
  try {
    const response = await axiosInstance.post("users/me/cookie-consent/", {
      essential: consent.essential,
      analytics: consent.analytics,
      marketing: consent.marketing,
      preferences: consent.preferences,
      timestamp: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    // If user is not authenticated or endpoint doesn't exist, fail silently
    if (error.response?.status === 401 || error.response?.status === 404) {
      console.warn(
        "Cannot save cookie consent to backend (user not authenticated or endpoint not available)"
      );
      return null;
    }
    console.error("Error saving cookie consent to backend:", error);
    throw error;
  }
};

/**
 * Update user's cookie consent preferences on backend
 * @param {Object} consent - Updated cookie consent preferences
 * @returns {Promise<Object>} Updated consent preferences
 */
export const updateCookieConsentOnBackend = async (consent) => {
  try {
    const response = await axiosInstance.patch("users/me/cookie-consent/", {
      essential: consent.essential,
      analytics: consent.analytics,
      marketing: consent.marketing,
      preferences: consent.preferences,
      timestamp: new Date().toISOString(),
    });
    return response.data;
  } catch (error) {
    // Try POST if PATCH fails (for new records)
    if (error.response?.status === 404) {
      return saveCookieConsentToBackend(consent);
    }
    // If user is not authenticated, fail silently
    if (error.response?.status === 401) {
      console.warn("Cannot update cookie consent on backend (user not authenticated)");
      return null;
    }
    console.error("Error updating cookie consent on backend:", error);
    throw error;
  }
};

/**
 * Sync cookie consent between local storage and backend
 * This should be called after user login to sync preferences
 * @param {Object} localConsent - Local consent preferences from localStorage
 * @returns {Promise<Object>} Synced consent preferences
 */
export const syncCookieConsent = async (localConsent) => {
  try {
    // Get backend consent
    const backendConsent = await getCookieConsentFromBackend();

    // If no backend consent exists, save local consent to backend
    if (!backendConsent) {
      if (localConsent) {
        await saveCookieConsentToBackend(localConsent);
      }
      return localConsent;
    }

    // If no local consent exists, use backend consent
    if (!localConsent) {
      return backendConsent;
    }

    // If both exist, use the most recent one
    const localTimestamp = new Date(localStorage.getItem("ts_cookie_consent_timestamp") || 0);
    const backendTimestamp = new Date(backendConsent.timestamp || 0);

    if (backendTimestamp > localTimestamp) {
      // Backend is more recent, use it
      return backendConsent;
    } else {
      // Local is more recent, update backend
      await updateCookieConsentOnBackend(localConsent);
      return localConsent;
    }
  } catch (error) {
    console.error("Error syncing cookie consent:", error);
    // Return local consent if sync fails
    return localConsent;
  }
};
